import React from 'react'
import { useEffect, useMemo, useState, useRef } from 'preact/hooks'
import { Button, Link, Modal } from '@nespresso-design-system/components'
import { getPBProps, sanitizeHtml } from '@content/helpers/src/utils.ts'
import { PbPropsProvider } from '@content/helpers/src/context.tsx'
import { isLoggedIn as checkLoginStatus } from '@content/helpers/src/dataLayer'
import { mgmInteraction } from '@content/helpers/src/gtmEvents'
import PictoTextDescription from '@commons/picto-text-description/PictoTextDescription.tsx'
import SectionHeader from '@commons/section-header/SectionHeader.tsx'
import { PBProps } from '@components/member-get-member/mgm-referral/types.ts'
import Steps from '@commons/steps/Steps.tsx'
import styles from './MgmReferral.module.scss'
import TextMessage from '@commons/text-message/TextMessage.tsx'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker.ts'
import { getTestId } from '@content/helpers/src/utils'

type ReferralProps = {
    data: string
}

const MgmReferral = ({ data }: ReferralProps) => {
    const pbProps = getPBProps<PBProps>(data)
    const { copywriting, layout, campaign } = pbProps as PBProps
    const containerRef = useRef(null)

    const [referralCode, setReferralCode] = useState('')

    useImpressionTracker(containerRef, campaign)

    useEffect(() => {
        let attempts = 0
        const maxAttempts = 20
        const interval = setInterval(() => {
            attempts++
            const code = window?.padl?.user?.clubMember?.referralCode
            if (code) {
                try {
                    setReferralCode(code)
                    clearInterval(interval)
                } catch (err) {
                    if (attempts >= maxAttempts) {
                        console.error('Error setting referral code:', err)
                        clearInterval(interval)
                    }
                }
            } else if (attempts >= maxAttempts) {
                clearInterval(interval)
            }
        }, 200)

        return () => clearInterval(interval)
    }, [])

    const classes = [
        styles.shareReferalCode,
        layout?.contrast ? styles[`shareReferalCode--${layout.contrast}`] : ''
    ].join(' ')

    const isLoggedIn = checkLoginStatus()

    const pictoItems = useMemo(() => {
        return copywriting.pictoTextDescription.map(item => {
            if (
                isLoggedIn &&
                typeof item.description === 'string' &&
                item.description.includes('referral_code')
            ) {
                return {
                    ...item,
                    description: item.description.replace(
                        'referral_code',
                        referralCode
                    )
                }
            }

            return item
        })
    }, [copywriting.pictoTextDescription, isLoggedIn, referralCode])

    const plainTextMessage = referralCode
        ? `${copywriting.textMessage?.text || ''} ${referralCode}`
        : copywriting.textMessage?.text || ''

    const [isModalOpen, setIsModalOpen] = useState(false)

    const openModal = (
        e: React.MouseEvent<HTMLAnchorElement>,
        clickedLabel: string
    ) => {
        mgmInteraction(clickedLabel)
        e.preventDefault()
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
    }

    const handleCtaClick = (label: string) => {
        mgmInteraction(label)
    }

    return (
        <PbPropsProvider value={pbProps}>
            <section
                id={copywriting.anchorId || 'referral-section'}
                className={classes}
                aria-label={copywriting.heading}
                data-testid={getTestId('mgm-referral')}>
                <SectionHeader
                    headline={copywriting.heading}
                    description={
                        <p
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(copywriting.subhead)
                            }}
                        />
                    }
                    align={copywriting.align}
                    className={styles.shareReferalCode__header}
                />

                <PictoTextDescription items={pictoItems} />

                {isLoggedIn ? (
                    <Steps
                        heading={copywriting.stepsLoggedIn.heading}
                        items={copywriting.stepsLoggedIn.items}
                    />
                ) : (
                    <Steps
                        heading={copywriting.stepsLoggedOut.heading}
                        items={copywriting.stepsLoggedOut.items}
                    />
                )}

                <Modal
                    id={copywriting?.modal?.id}
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    heading={copywriting?.modal?.heading}
                    description={
                        <p
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(
                                    copywriting?.modal?.description
                                )
                            }}
                        />
                    }
                    closeBtnA11yLabel={
                        copywriting?.modal?.translations?.closeBtnA11yLabel
                    }
                    closeBtnA11yTitle={
                        copywriting?.modal?.translations?.closeBtnA11yTitle
                    }
                />

                {isLoggedIn && copywriting.textMessage ? (
                    <div>
                        <TextMessage
                            data={copywriting.textMessage}
                            referral={referralCode}
                        />
                        <div className={styles.shareReferalCode__doubleCta}>
                            <div
                                className={
                                    styles.shareReferalCode__doubleCta__title
                                }>
                                <h3>Refer a friend by</h3>
                            </div>

                            <div
                                className={
                                    styles.shareReferalCode__doubleCta__buttons
                                }>
                                <Button
                                    label={'WhatsApp'}
                                    variant={'secondary'}
                                    href={`https://wa.me/?text=${encodeURIComponent(plainTextMessage)}`}
                                    target={'_blank'}
                                    aria-label="Send this message via WhatsApp"
                                    onClick={() => mgmInteraction('WhatsApp')}
                                />
                                <Button
                                    label={'Email'}
                                    variant={'secondary'}
                                    href={`mailto:?subject=Exclusive Nespresso Offer Just for You! Let’s share our Coffee Love&body=${encodeURIComponent(plainTextMessage)}`}
                                    target={'_blank'}
                                    aria-label="Send this message via Email"
                                    onClick={() => mgmInteraction('Email')}
                                />
                            </div>
                            <Link
                                className={
                                    styles.shareReferalCode__conditionsLink
                                }
                                label={copywriting?.link?.label}
                                href={copywriting?.link?.link || '#'}
                                variant="black"
                                onClick={e =>
                                    openModal(e, 'click - terms and conditions')
                                }
                            />
                        </div>
                    </div>
                ) : (
                    copywriting.cta && (
                        <div className={styles.shareReferalCode__cta}>
                            <div>
                                <Button
                                    label={copywriting.cta.label}
                                    variant={'secondary'}
                                    href={copywriting.cta.link}
                                    onClick={() =>
                                        handleCtaClick(copywriting.cta.label)
                                    }
                                />
                            </div>
                            <div>
                                <Link
                                    className={
                                        styles.shareReferalCode__conditionsLink
                                    }
                                    label={copywriting?.link?.label}
                                    href={copywriting?.link?.link || '#'}
                                    variant="black"
                                    onClick={e =>
                                        openModal(
                                            e,
                                            'click - terms and conditions'
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )
                )}

                {copywriting.imageBlockSeparator && (
                    <div
                        className={`${styles.shareReferalCode__imageBlockSeparator} ${
                            copywriting.imageBlockSeparator.position === 'right'
                                ? styles.shareReferalCode__imageBlockSeparator__right
                                : ''
                        }`}>
                        {copywriting.imageBlockSeparator.image && (
                            <img
                                src={copywriting.imageBlockSeparator.image}
                                className={
                                    styles.shareReferalCode__imageBlockSeparator__image
                                }
                                alt=""
                                aria-hidden="true"
                            />
                        )}
                    </div>
                )}
            </section>
        </PbPropsProvider>
    )
}

export default MgmReferral
